import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import type {
  MarketsDto,
  FavoriteMarketDto,
  FavoriteMarketsDto,
} from '@b-stock/notification-api-client'

import api from '@api/notification'

const useMarkets = () => {
  return useQuery({
    queryKey: ['markets'],
    queryFn: () => api.getMarkets(),
  })
}

const getKey = (personId: string) => ['favoriteMarkets', personId]
const useFavoriteMarkets = (personId: string) => {
  if (!personId) {
    throw new Error('Unable to extract a personId from the store')
  }

  return useQuery({
    queryKey: getKey(personId),
    queryFn: () => api.getFavorites(personId),
  })
}

const useAddFavoriteMarket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      marketId: string
      personId: string
      accountId: string
      listingEmailsEnabled: boolean
    }) => api.createFavorite(payload), // Just pass the payload once
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: getKey(variables.personId),
      })
    },
  })
}

const useUpdateFavoriteMarket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { _id: string; listingEmailsEnabled: boolean }) =>
      api.updateFavorite(payload),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['favoriteMarkets'],
      })
    },
  })
}

const useRemoveFavoriteMarket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (favoriteId: string) => api.deleteFavorite(favoriteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['favoriteMarkets'],
      })
    },
  })
}

export {
  useMarkets,
  useFavoriteMarkets,
  useAddFavoriteMarket,
  useUpdateFavoriteMarket,
  useRemoveFavoriteMarket,
}
export type { MarketsDto, FavoriteMarketsDto, FavoriteMarketDto }
